import { useFrame } from "@react-three/fiber";
import { useRef, type MutableRefObject, type ReactNode } from "react";
import { BoxGeometry, type Group } from "three";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";
import { dampAngle, type PresentationPose } from "./motion";

const VOXEL_BOX_GEOMETRY = new BoxGeometry(1, 1, 1);

interface PartProps {
  position: [number, number, number];
  scale: [number, number, number];
  color: string;
  rotation?: [number, number, number];
}

function Part({ position, scale, color, rotation = [0, 0, 0] }: PartProps) {
  return (
    <mesh position={position} scale={scale} rotation={rotation} castShadow geometry={VOXEL_BOX_GEOMETRY} dispose={null}>
      <meshStandardMaterial color={color} roughness={0.72} metalness={0.02} />
    </mesh>
  );
}

function Eyes({ y = 0.42, z = 0.48, spacing = 0.25 }: { y?: number; z?: number; spacing?: number }) {
  return (
    <>
      <Part position={[-spacing, y, z]} scale={[0.12, 0.16, 0.08]} color="#172033" />
      <Part position={[spacing, y, z]} scale={[0.12, 0.16, 0.08]} color="#172033" />
    </>
  );
}

function AnimalFace({ skinId }: { skinId: string }): ReactNode {
  if (skinId === "yak") return <>
    <Part position={[0, 0.15, 0]} scale={[1.15, 0.9, 1]} color="#75452e" />
    <Part position={[0, 0.18, 0.54]} scale={[0.7, 0.42, 0.18]} color="#efe1c7" />
    <Part position={[-0.63, 0.58, 0]} scale={[0.38, 0.14, 0.15]} color="#f4ead9" rotation={[0, 0, -0.45]} />
    <Part position={[0.63, 0.58, 0]} scale={[0.38, 0.14, 0.15]} color="#f4ead9" rotation={[0, 0, 0.45]} />
    <Eyes y={0.38} z={0.56} spacing={0.27} />
  </>;
  if (skinId === "duck") return <>
    <Part position={[0, 0.18, 0]} scale={[1, 0.95, 1]} color="#ffd60a" />
    <Part position={[0, 0.13, 0.62]} scale={[0.6, 0.24, 0.3]} color="#ff8c1a" />
    <Eyes y={0.45} z={0.52} spacing={0.24} />
  </>;
  if (skinId === "cat") return <>
    <Part position={[0, 0.18, 0]} scale={[1, 0.95, 1]} color="#ff9f68" />
    <Part position={[-0.34, 0.82, 0]} scale={[0.28, 0.42, 0.28]} color="#ff9f68" rotation={[0, 0, 0.32]} />
    <Part position={[0.34, 0.82, 0]} scale={[0.28, 0.42, 0.28]} color="#ff9f68" rotation={[0, 0, -0.32]} />
    <Part position={[0, 0.18, 0.55]} scale={[0.45, 0.32, 0.14]} color="#fff0db" />
    <Eyes y={0.45} z={0.53} />
  </>;
  if (skinId === "pig") return <>
    <Part position={[0, 0.18, 0]} scale={[1, 0.95, 1]} color="#ff8fab" />
    <Part position={[-0.34, 0.72, 0]} scale={[0.28, 0.3, 0.25]} color="#ffc2d1" rotation={[0, 0, 0.18]} />
    <Part position={[0.34, 0.72, 0]} scale={[0.28, 0.3, 0.25]} color="#ffc2d1" rotation={[0, 0, -0.18]} />
    <Part position={[0, 0.18, 0.58]} scale={[0.54, 0.34, 0.18]} color="#ffc2d1" />
    <Part position={[-0.12, 0.18, 0.68]} scale={[0.07, 0.09, 0.05]} color="#c9184a" />
    <Part position={[0.12, 0.18, 0.68]} scale={[0.07, 0.09, 0.05]} color="#c9184a" />
    <Eyes y={0.46} z={0.53} />
  </>;
  if (skinId === "frog") return <>
    <Part position={[0, 0.16, 0]} scale={[1.05, 0.85, 1]} color="#70e000" />
    <Part position={[-0.33, 0.72, 0.26]} scale={[0.32, 0.34, 0.32]} color="#ccff33" />
    <Part position={[0.33, 0.72, 0.26]} scale={[0.32, 0.34, 0.32]} color="#ccff33" />
    <Eyes y={0.75} z={0.44} spacing={0.33} />
  </>;
  if (skinId === "corgi") return <>
    <Part position={[0, 0.15, 0]} scale={[1.1, 0.9, 1]} color="#e98b2a" />
    <Part position={[-0.34, 0.75, 0]} scale={[0.3, 0.42, 0.28]} color="#e98b2a" rotation={[0, 0, 0.22]} />
    <Part position={[0.34, 0.75, 0]} scale={[0.3, 0.42, 0.28]} color="#e98b2a" rotation={[0, 0, -0.22]} />
    <Part position={[0, 0.18, 0.55]} scale={[0.55, 0.52, 0.16]} color="#fff3d6" />
    <Eyes y={0.45} z={0.54} />
  </>;
  if (skinId === "panda") return <>
    <Part position={[0, 0.16, 0]} scale={[1, 0.95, 1]} color="#f8fafc" />
    <Part position={[-0.42, 0.7, 0]} scale={[0.3, 0.34, 0.3]} color="#111827" />
    <Part position={[0.42, 0.7, 0]} scale={[0.3, 0.34, 0.3]} color="#111827" />
    <Part position={[-0.24, 0.43, 0.54]} scale={[0.28, 0.27, 0.1]} color="#111827" rotation={[0, 0, -0.22]} />
    <Part position={[0.24, 0.43, 0.54]} scale={[0.28, 0.27, 0.1]} color="#111827" rotation={[0, 0, 0.22]} />
    <Eyes y={0.45} z={0.61} />
  </>;
  if (skinId === "unicorn") return <>
    <Part position={[0, 0.16, 0]} scale={[1, 0.95, 1]} color="#fdf4ff" />
    <Part position={[-0.35, 0.75, 0]} scale={[0.27, 0.4, 0.24]} color="#c084fc" rotation={[0, 0, 0.25]} />
    <Part position={[0.35, 0.75, 0]} scale={[0.27, 0.4, 0.24]} color="#c084fc" rotation={[0, 0, -0.25]} />
    <Part position={[0, 0.98, 0.05]} scale={[0.16, 0.52, 0.16]} color="#22d3ee" rotation={[0.25, 0, 0]} />
    <Eyes y={0.45} z={0.53} />
  </>;
  return <>
    <Part position={[0, 0.18, 0]} scale={[1, 0.9, 1]} color="#ffca3a" />
    <Eyes y={0.43} z={0.53} />
  </>;
}

function FoodFace({ skinId }: { skinId: string }): ReactNode {
  if (skinId === "burger") return <>
    <Part position={[0, 0.5, 0]} scale={[1.15, 0.28, 1]} color="#f6bd60" />
    <Part position={[0, 0.2, 0]} scale={[1.08, 0.18, 0.95]} color="#6f4518" />
    <Part position={[0, 0.02, 0]} scale={[1.1, 0.12, 0.98]} color="#6a994e" />
    <Part position={[0, -0.16, 0]} scale={[1.15, 0.24, 1]} color="#f6bd60" />
    <Eyes y={0.23} z={0.51} />
  </>;
  if (skinId === "sushi") return <>
    <Part position={[0, 0.18, 0]} scale={[1.05, 0.9, 1]} color="#f8f9fa" />
    <Part position={[0, 0.18, 0]} scale={[1.12, 0.46, 1.08]} color="#1f2937" />
    <Part position={[0, 0.68, 0]} scale={[1, 0.22, 0.96]} color="#fb7185" />
    <Eyes y={0.22} z={0.58} />
  </>;
  return <>
    <Part position={[0, 0.12, 0]} scale={[1.05, 0.76, 1]} color="#d99b73" />
    <Part position={[0, 0.5, 0]} scale={[1.08, 0.22, 1.02]} color="#f472b6" />
    <Part position={[-0.3, 0.66, 0.15]} scale={[0.12, 0.07, 0.05]} color="#67e8f9" rotation={[0, 0, 0.5]} />
    <Part position={[0.2, 0.66, 0.25]} scale={[0.12, 0.07, 0.05]} color="#fef08a" rotation={[0, 0, -0.4]} />
    <Eyes y={0.25} z={0.54} />
  </>;
}

export function VoxelSkin({ skinId, heading = 0, poseRef, scale = 1 }: {
  skinId: string;
  heading?: number;
  poseRef?: MutableRefObject<PresentationPose>;
  scale?: number;
}) {
  const group = useRef<Group>(null);
  const initialized = useRef(false);
  useFrame(({ clock }, delta) => {
    if (!group.current) return;
    const presentedHeading = poseRef
      ? Math.atan2(poseRef.current.headingY, poseRef.current.headingX)
      : heading;
    const targetHeading = -presentedHeading + Math.PI / 2;
    if (!initialized.current) {
      group.current.rotation.y = targetHeading;
      initialized.current = true;
    } else {
      group.current.rotation.y = dampAngle(
        group.current.rotation.y,
        targetHeading,
        YAK_TRAILS_CLIENT_TUNING.player.skinHeadingSmoothing,
        delta,
      );
    }
    group.current.position.y = Math.sin(clock.elapsedTime * YAK_TRAILS_CLIENT_TUNING.player.idleBobSpeed)
      * YAK_TRAILS_CLIENT_TUNING.player.idleBobAmount;
    group.current.rotation.z = Math.sin(clock.elapsedTime * YAK_TRAILS_CLIENT_TUNING.player.idleTwistSpeed)
      * YAK_TRAILS_CLIENT_TUNING.player.idleTwistAmount;
  });
  const food = skinId === "burger" || skinId === "sushi" || skinId === "donut";
  return (
    <group ref={group} scale={scale}>
      {food ? <FoodFace skinId={skinId} /> : <AnimalFace skinId={skinId} />}
    </group>
  );
}
